/**
 * Shared Zod schema for a single choice-option entry consumed by the
 * `single_choice` and `multi_choice` renderers (and any future
 * choice-style renderer in NX-9302..NX-9304).
 *
 * The upstream `@nextday/funnel-ui` types `SingleChoiceOption` and
 * `MultiChoiceOption` are structurally identical; keeping a single Zod
 * source of truth here prevents the two renderers' validation surfaces
 * from silently diverging when a field is added (e.g. analytics tag,
 * a11y hint).
 */
import { z } from "zod";

import { urlOrTemplate } from "@survey-kit/urlOrTemplate";

export const choiceOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  imageUrl: urlOrTemplate.optional(),
  /** CSS `object-position` for the cover image (e.g. "top", "center", "bottom"). Defaults to "center". */
  imagePosition: z.string().optional(),
  /** Optional image tile aspect ratio; unset preserves the layout's built-in ratio. */
  imageAspect: z.enum(["square", "portrait", "landscape"]).optional(),
  iconLeading: z.string().optional(),
  badge: z
    .object({
      label: z.string().min(1),
      tone: z.enum(["default", "accent", "warning", "success"]).optional(),
    })
    .optional(),
  disabled: z.boolean().optional(),
});

export type ChoiceOption = z.infer<typeof choiceOptionSchema>;
